import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router-dom'
import { FleetSection, Notice } from '../../../../shared/sso-workspace/fleet-page'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { formatAgo, formatDateTime } from '../../../../shared/sso-workspace/format'
import { serverMessage } from '../../../../shared/error/server-message'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { OrgPicker } from '../../../../shared/org-search/org-picker'
import { useOrganizationName } from '../../../../shared/org-search/use-organization-names'
import { clearSwitch, getCatalogue, getSwitches, putSwitch } from '../../../../api-new/state/notify'
import { CATEGORIES, categoryLabel } from '../../../../shared/notify/words'
import { ReasonDialog } from './reason-dialog'
import { useNotifyOpsAccess } from './access'

const CHANNEL_LABEL = { inapp: 'In-app', email: 'Email', push: 'Push' }
const PLATFORM_LABEL = { ios: 'iOS', android: 'Android', web: 'Web', macos: 'Mac', visionos: 'visionOS' }
const FEATURE_LABEL = { push_images: 'Images in push' }
const SOURCE_LABEL = { ceiling: 'Forced off by this deployment', override: 'Organization override', global: 'Global setting', default: 'Default' }

export const keyLabel = k => {
  if (k.kind === 'channel') return CHANNEL_LABEL[k.name] || k.name
  if (k.kind === 'platform') return PLATFORM_LABEL[k.name] || k.name
  if (k.kind === 'feature') return FEATURE_LABEL[k.name] || k.name
  return k.name
}

// A setting's last change: who, why and when.
const changeText = entry =>
  entry ? `${entry.updatedByName || `Login ${entry.updatedByLoginId}`}${entry.reason ? `: ${entry.reason}` : ''}` : ''

// The global value of a key: ceilings win, then the global setting, else on.
export const globalState = (key, data) => {
  const ceiling = (data.ceilings || []).find(c => c.key === key)
  const entry = (data.global || []).find(g => g.key === key)
  if (ceiling) return { enabled: false, source: 'ceiling', ceiling, entry }
  if (entry) return { enabled: entry.enabled, source: 'global', entry }
  return { enabled: true, source: 'default' }
}

const StatePill = ({ enabled, source }) =>
  <Pill tone={enabled ? 'ok' : 'bad'}>{enabled ? 'On' : 'Off'}{source === 'default' ? ' (default)' : ''}</Pill>

// What each pending change asks before it is sent (the security
// confirmation first, then the reason dialog with any channel warning).
const needsSecurityStep = p => p.action === 'set' && !p.enabled && p.key.security
const stopsSecurity = p => p.action === 'set' && !p.enabled && (p.key.kind === 'channel' || p.key.kind === 'platform')

const SecurityStep = ({ pending, onContinue, onCancel }) => {
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') { e.stopPropagation(); onCancel() } }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onCancel])
  return createPortal(
    <div className="sw-drawer justify-content-center align-items-center" onClick={e => { if (e.target === e.currentTarget) onCancel() }}>
      <div className="sw-dialog" role="alertdialog" aria-modal="true" aria-labelledby="nf-sec-title" aria-describedby="nf-sec-body">
        <h2 className="h5" id="nf-sec-title">{`Switch off ${pending.key.name}?`}</h2>
        <p className="sw-hint" id="nf-sec-body">
          {`This is a security notification. Switching it off means ${pending.organizationId ? "this organization's people" : 'nobody'} will no longer be warned about it (for example a sign-in from a new device). Only do this during an incident or a confirmed fault.`}
        </p>
        <div className="sw-dialog-actions">
          <button type="button" className="btn sw-btn" onClick={onCancel}>Cancel</button>
          <button type="button" className="btn sw-btn-danger" onClick={onContinue}>I understand, continue</button>
        </div>
      </div>
    </div>,
    document.body
  )
}

const dialogWords = p => {
  const what = `${p.key.kind === 'type' ? 'notification type' : p.key.kind === 'platform' ? 'push platform' : p.key.kind === 'feature' ? 'feature' : 'channel'} ${keyLabel(p.key)}`
  const scope = p.organizationId ? ' for this organization' : ' for everyone'
  if (p.action === 'clear') return { title: `Inherit ${what}?`, body: `${p.organizationId ? 'The organization override is removed and it follows the global setting again.' : 'The global setting is removed and the default (on) applies.'}`, confirmText: 'Inherit' }
  return {
    title: `Switch ${p.enabled ? 'on' : 'off'} ${what}${scope}?`,
    body: p.enabled ? 'This takes effect on every server and app shortly after you confirm.' : 'Nothing of this kind is sent once the change has reached every server. This takes effect shortly after you confirm.',
    confirmText: p.enabled ? 'Switch on' : 'Switch off'
  }
}

const SwitchRows = ({ keys, rowFor, canUpdate, onToggle, onSelect, idPrefix }) =>
  <table className="nf-table">
    <thead>
      <tr><th>Setting</th><th>State</th><th>Source</th><th>Last change</th><th><span className="visually-hidden">Change</span></th></tr>
    </thead>
    <tbody>
      {keys.map(k => {
        const r = rowFor(k)
        return (
          <tr key={k.key} id={idPrefix ? `${idPrefix}${k.key}` : undefined}>
            <td>
              <span className="sw-mono">{keyLabel(k)}</span>
              {k.security && <> <Pill tone="warn" title="Security notification: switching it off needs a confirmation.">Security</Pill></>}
            </td>
            <td><StatePill enabled={r.enabled} source={r.source} /></td>
            <td>{r.sourceText}</td>
            <td>
              {r.entry
                ? <span title={formatDateTime(r.entry.updatedAt)}>{changeText(r.entry)} <span className="sw-hint">{formatAgo(r.entry.updatedAt)}</span></span>
                : <span className="sw-hint">Never changed</span>}
            </td>
            <td>
              {r.locked
                ? <span className="sw-hint">Locked</span>
                : onToggle
                  ? <span className="form-check form-switch m-0"><input type="checkbox" role="switch" className="form-check-input" aria-label={`${keyLabel(k)} (${k.kind})`} checked={r.enabled} disabled={!canUpdate} onChange={e => onToggle(k, e.target.checked)} /></span>
                  : <select className="form-select form-select-sm" aria-label={`${keyLabel(k)} (${k.kind}) for this organization`} value={r.choice} disabled={!canUpdate} onChange={e => onSelect(k, e.target.value)}>
                      <option value="inherit">Inherit</option>
                      <option value="on">On</option>
                      <option value="off">Off</option>
                    </select>}
            </td>
          </tr>
        )
      })}
    </tbody>
  </table>

// Groups the keys the way the page reads: channels, push platforms, then
// notification types by category (a type's category comes from the
// catalogue; one it does not know goes under Other).
export const groupKeys = (keys, categories) => {
  const types = keys.filter(k => k.kind === 'type')
  const byCategory = new Map()
  types.forEach(k => {
    const c = categories[k.name] || 'other'
    if (!byCategory.has(c)) byCategory.set(c, [])
    byCategory.get(c).push(k)
  })
  const order = [...CATEGORIES, 'other'].filter(c => byCategory.has(c))
  return {
    channels: keys.filter(k => k.kind === 'channel'),
    platforms: keys.filter(k => k.kind === 'platform'),
    features: keys.filter(k => k.kind === 'feature'),
    types: order.map(c => ({ category: c, label: c === 'other' ? 'Other' : categoryLabel(c), keys: byCategory.get(c) }))
  }
}

const OrgHeading = ({ id }) => {
  const name = useOrganizationName(id)
  return name ? `${name} (${id})` : `Organization ${id}`
}

// Rollout (owner 2026-10-13: roll out as we want, globally enabling and
// disabling channels): every switch with its state, source and last change;
// the deployment's ceilings, locked; and per-organization overrides.
// SYS_NOTIFY read shows everything, update changes it (always with a
// reason). A security notification asks twice before it goes off.
export const RolloutTab = () => {
  const access = useNotifyOpsAccess()
  const location = useLocation()
  const [data, setData] = useState(null)
  const [categories, setCategories] = useState({})
  const [orgId, setOrgId] = useState('')
  const [orgData, setOrgData] = useState(null)
  const [error, failure, setError] = useErrorLine()
  const [pending, setPending] = useState(null) // { key, organizationId, action, enabled, confirmed }
  const [busy, setBusy] = useState(false)
  const [dialogError, dialogFailure, setDialogError] = useErrorLine()

  const loadGlobal = useCallback(async () => {
    try {
      setData(await getSwitches())
      setError(null)
    } catch (err) {
      setError(serverMessage(err, "The switches couldn't be loaded."), err)
    }
  }, [setError])

  const loadOrg = useCallback(async id => {
    if (!id) { setOrgData(null); return }
    try {
      setOrgData(await getSwitches(id))
    } catch (err) {
      setError(serverMessage(err, "The organization's switches couldn't be loaded."), err)
    }
  }, [setError])

  useEffect(() => { loadGlobal() }, [loadGlobal])
  useEffect(() => { loadOrg(orgId) }, [loadOrg, orgId])
  useEffect(() => {
    let live = true
    getCatalogue().then(c => {
      if (!live || !c) return
      setCategories(Object.fromEntries((c.types || []).map(t => [t.type, t.category])))
    }).catch(() => {})
    return () => { live = false }
  }, [])

  // A link from the Catalogue (#type:course.published) scrolls to its row.
  useEffect(() => {
    if (!data || !location.hash) return
    const el = document.getElementById(`sw-${decodeURIComponent(location.hash.slice(1))}`)
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'center' })
  }, [data, location.hash])

  const keys = useMemo(() => (data ? data.keys || [] : []), [data])
  const groups = useMemo(() => groupKeys(keys, categories), [keys, categories])
  const seconds = data && data.cacheSeconds

  const globalRow = k => {
    const s = globalState(k.key, data)
    const sourceText = s.ceiling ? `${SOURCE_LABEL.ceiling} (${s.ceiling.by})` : SOURCE_LABEL[s.source]
    return { ...s, sourceText, locked: s.source === 'ceiling' }
  }

  const orgRow = k => {
    const eff = (orgData.effective || []).find(e => e.key === k.key) || { enabled: true, source: 'default' }
    const ov = (orgData.overrides || []).find(o => o.key === k.key && String(o.organizationId) === String(orgId))
    const ceiling = (orgData.ceilings || []).find(c => c.key === k.key)
    return {
      enabled: eff.enabled,
      source: eff.source,
      sourceText: ceiling ? `${SOURCE_LABEL.ceiling} (${ceiling.by})` : SOURCE_LABEL[eff.source] || eff.source,
      entry: ov,
      locked: eff.source === 'ceiling',
      choice: ov ? (ov.enabled ? 'on' : 'off') : 'inherit'
    }
  }

  const ask = p => { setDialogError(null); setPending(p) }
  const onToggle = (key, enabled) => ask({ key, organizationId: null, action: 'set', enabled })
  const onSelect = (key, choice) => {
    if (choice === 'inherit') ask({ key, organizationId: Number(orgId), action: 'clear' })
    else ask({ key, organizationId: Number(orgId), action: 'set', enabled: choice === 'on' })
  }

  const confirm = async reason => {
    setBusy(true)
    setDialogError(null)
    try {
      if (pending.action === 'clear') {
        await clearSwitch({ key: pending.key.key, ...(pending.organizationId ? { organizationId: pending.organizationId } : {}), reason })
      } else {
        await putSwitch({
          key: pending.key.key,
          ...(pending.organizationId ? { organizationId: pending.organizationId } : {}),
          enabled: pending.enabled,
          reason,
          confirmSecurity: Boolean(pending.confirmed)
        })
      }
      setPending(null)
      await Promise.all([loadGlobal(), loadOrg(orgId)])
    } catch (err) {
      setDialogError(serverMessage(err, "The change couldn't be saved."), err)
    } finally {
      setBusy(false)
    }
  }

  if (!data) {
    return error
      ? <><p className="text-danger small" role="alert">{error}</p><ErrorReference error={failure} /></>
      : <p className="sw-hint" role="status">Loading the switches…</p>
  }

  const canUpdate = access.canUpdate
  const overrides = data.overrides || []
  const words = pending ? dialogWords(pending) : null
  const securityStep = pending && needsSecurityStep(pending) && !pending.confirmed

  return (
    <>
      {error && <><p className="text-danger small" role="alert">{error}</p><ErrorReference error={failure} /></>}
      <Notice tone="info" role="status">
        {`Changes reach every server and app within about ${seconds} s. Apps re-check within a few minutes.`}
      </Notice>

      {(data.ceilings || []).length > 0 &&
        <FleetSection id="nf-ceilings" title="Forced off by this deployment" lede="These are switched off by the deployment's own settings. A switch here can't turn them back on.">
          <ul className="mb-0">
            {data.ceilings.map(c => <li key={c.key}>{`${c.key}: Forced off by this deployment (${c.by})`}</li>)}
          </ul>
        </FleetSection>}

      <FleetSection id="nf-channels" title="Channels" lede="Switching a channel off stops everything on it, security notifications included.">
        <SwitchRows keys={groups.channels} rowFor={globalRow} canUpdate={canUpdate} onToggle={onToggle} idPrefix="sw-" />
      </FleetSection>

      <FleetSection id="nf-platforms" title="Push platforms" lede="Switching a platform off stops push to those devices, security notifications included.">
        <SwitchRows keys={groups.platforms} rowFor={globalRow} canUpdate={canUpdate} onToggle={onToggle} idPrefix="sw-" />
      </FleetSection>

      {groups.features.length > 0 &&
        <FleetSection id="nf-features" title="Features" lede="Images in push shows a course's thumbnail on the lock screen. Turn it off for an organization that doesn't want course images shown there; the push still goes, without the image.">
          <SwitchRows keys={groups.features} rowFor={globalRow} canUpdate={canUpdate} onToggle={onToggle} idPrefix="sw-" />
        </FleetSection>}

      <FleetSection id="nf-types" title="Notification types" lede="A type that is off is never raised for anyone. Security types ask for a confirmation first.">
        {groups.types.map(g =>
          <div key={g.category} className="mb-3">
            <h3 className="h6 fw-semibold">{g.label}</h3>
            <SwitchRows keys={g.keys} rowFor={globalRow} canUpdate={canUpdate} onToggle={onToggle} idPrefix="sw-" />
          </div>)}
      </FleetSection>

      <FleetSection id="nf-orgs" title="Organization overrides" lede="Turn something on or off for one organization only, for a pilot or a fault. Inherit follows the global setting.">
        <div className="nf-toolbar">
          <span className="nf-org">
            <OrgPicker id="nf-rollout-org" label="Rollout organization" value={orgId} onChange={setOrgId} />
          </span>
        </div>
        {orgId && orgData
          ? <>
              <h3 className="h6 fw-semibold"><OrgHeading id={orgId} /></h3>
              <p className="sw-hint">What this organization gets now, and where each value comes from.</p>
              <SwitchRows keys={keys} rowFor={orgRow} canUpdate={canUpdate} onSelect={onSelect} />
            </>
          : <p className="sw-hint">Choose an organization to see its effective values and set an override.</p>}

        <h3 className="h6 fw-semibold mt-4">Every override</h3>
        {overrides.length === 0
          ? <p className="sw-hint">No organization has an override.</p>
          : <table className="nf-table">
              <thead><tr><th>Organization</th><th>Setting</th><th>State</th><th>Reason</th><th>Changed</th><th><span className="visually-hidden">Clear</span></th></tr></thead>
              <tbody>
                {overrides.map(o => {
                  const k = keys.find(x => x.key === o.key) || { key: o.key, kind: o.key.split(':')[0], name: o.key.split(':')[1] }
                  return (
                    <tr key={`${o.organizationId}-${o.key}`}>
                      <td>{o.organizationName || `Organization ${o.organizationId}`}</td>
                      <td className="sw-mono">{o.key}</td>
                      <td><StatePill enabled={o.enabled} /></td>
                      <td>{o.reason}</td>
                      <td><span title={formatDateTime(o.updatedAt)}>{formatAgo(o.updatedAt)}</span></td>
                      <td>
                        {canUpdate &&
                          <button type="button" className="btn btn-sm sw-btn" aria-label={`Clear override ${o.key} for ${o.organizationName || o.organizationId}`}
                            onClick={() => ask({ key: k, organizationId: o.organizationId, action: 'clear' })}>Clear</button>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>}
      </FleetSection>

      {pending && securityStep &&
        <SecurityStep pending={pending} onCancel={() => setPending(null)} onContinue={() => setPending(p => ({ ...p, confirmed: true }))} />}
      <ReasonDialog
        open={Boolean(pending) && !securityStep}
        required
        danger={Boolean(pending) && pending.action === 'set' && !pending.enabled}
        title={words ? words.title : ''}
        body={words ? words.body : ''}
        confirmText={words ? words.confirmText : ''}
        busy={busy}
        error={dialogError}
        failure={dialogFailure}
        onConfirm={confirm}
        onCancel={() => setPending(null)}
      >
        {pending && stopsSecurity(pending) &&
          <p className="text-danger small" role="note">
            {pending.key.kind === 'platform'
              ? 'This also stops security notifications (such as sign-in alerts) on this platform.'
              : 'This also stops security notifications (such as sign-in alerts) on this channel.'}
          </p>}
      </ReasonDialog>
    </>
  )
}
